'use strict';
/**
 * Runtime loader + drawer for the baked Rookie hull+turret sprite atlases
 * (see public/tank-bake.html/.js, wild-percolating-breeze.md PHASE 1+2).
 * Classic script (no import/export) sharing the page's global scope with
 * sound.js/client.js - same convention as SoundManager in sound.js - since
 * client.js/sound.js are deliberately kept unbundled (see scripts/
 * build.mjs) and can't `import` a module.
 *
 * Both layers are baked from the same fixed camera (see tank-bake.js), so a
 * pixel within a hull frame and a pixel within a turret frame land on the
 * same screen position whenever their frame-local offsets from their
 * respective baked `anchor` match up - that's the whole compositing trick
 * below: draw the hull normally (as PHASE 1 did), then compute where the
 * CURRENT hull heading's turret mount point (manifest.hull.headings[h]
 * .turretMount, baked per heading since the mount orbits with the hull)
 * lands on screen, and draw the turret frame (chosen independently, nearest
 * to the live turretAngle) offset so ITS OWN pivot pixel
 * (manifest.turret.anchor - constant, since the turret bake only rotates
 * the pivot in place, never moves it - see tank-bake.js) lands exactly
 * there.
 *
 * PHASE 3: live pupils. Only the white sclera is baked into the hull sprite
 * (tank-bake.js hides both pupil meshes for every bake pass) - the pupil is
 * drawn fresh every frame on top of the composite, as a small dark circle at
 * the current hull heading's baked eye-socket pixel (manifest.hull
 * .headings[h].eyeLeft/eyeRight), offset toward the turret's gaze direction.
 * Reuses the exact formula already proven in the live 3D version,
 * gltf-tank-kit.js's buildEyeRig() `update()`:
 * `px = Math.sin(gazeYaw) * 0.05 * frontWidth`, gazeYaw = the turret's yaw
 * relative to the chassis (here: turretAngle - hullAngle, both already
 * screen-space angles). See drawPupils() below for the pixel-space
 * translation of `frontWidth`/the offset axis - and its sign comment for why
 * the ported formula needed a sign flip once checked against play-testing.
 *
 * PHASE 4 (this file, now): recoil kick. The turret sprite translates a few
 * pixels backward along its own (already-known-absolute) barrel direction
 * for a brief moment after a shot, eased back out - a pure translation of
 * the existing rotated frame, no new bake. See updateRecoil() below.
 */
const TankSprites = (() => {
  const HULL_ATLAS_URL = 'sprites/rookie-hull.png';
  const TURRET_ATLAS_URL = 'sprites/rookie-turret.png';
  const MANIFEST_URL = 'sprites/rookie-manifest.json';

  // How large a baked frame is drawn relative to its native FRAME_SIZE
  // (160px, see tank-bake.js), before the game's own ZOOM multiplies it -
  // tuned against the old primitive hull's ~40x28 (44x38 with treads)
  // local-unit footprint, still the relevant target since TANK_RADIUS/
  // COLLISION_DIST (server/index.js) weren't part of this re-angle change
  // (the baked frame carries a lot of transparent margin, needed so no
  // heading ever clips - see tank-bake.js's cornerR/halfHeight fit - so the
  // drawn size has to be noticeably bigger than the frame's own content
  // bounding box, not 1:1 with FRAME_SIZE). Re-measured at each elevation
  // the user reviewed: ~33deg gave a wider-than-tall silhouette; ~85deg
  // (near-orthographic top-down, "zu viel" - too much) pushed content bbox
  // to ~94x94px average across the 32 headings (up to ~103x105px at the
  // diagonal worst-case headings) versus a 160x160 frame - genuinely
  // square/top-down, unlike the shallower angles. ~69deg (see tank-bake.js's
  // viewDir comment) measured essentially the same: ~94x91px average
  // (up to 103x106px worst-case). User's latest pick, ~60deg, measures the
  // same again: ~94x89px average (up to 103x102px worst-case) - the bake
  // camera's fit-to-bounding-box distance (tank-bake.js's distH) is driven
  // purely by the tank's horizontal footprint radius and is elevation-
  // independent, so the same 0.45 scale still lands the width in the same
  // ~42-unit spot inside the 40-44 target band across all four elevations
  // baked so far. Re-measured and confirmed still correct at 0.45 - no
  // change needed this round. The turret layer shares this exact scale (and
  // the hull's own FRAME_SIZE) since both were baked from the identical
  // camera/fit - see tank-bake.js.
  const SPRITE_SCALE = 0.45;

  let manifest = null;
  let hullImage = null;
  let hullImageReady = false;
  let hullFrameByKey = null; // `${headingIndex}_${colorIndex}` -> {x,y,w,h}
  let turretImage = null;
  let turretImageReady = false;
  let turretFrameByKey = null; // `${headingIndex}_${colorIndex}` -> {x,y,w,h}

  function frameKey(headingIndex, colorIndex) {
    return headingIndex + '_' + colorIndex;
  }

  // PHASE 4: recoil kick. `opts.stopped` (client.js's shot-freeze flag,
  // client.js:1552/1558) is already exactly "this tank fired and hasn't
  // finished its post-shot freeze yet" - its RISING edge (false -> true) is
  // the fire moment itself, since client.js sets it in the very same step
  // that fires (localTank.stoppedUntil = now + cfg.shotStopMs right when the
  // shot is sent, client.js:780; server-driven p.stopped for remote tanks
  // follows the same shotStopMs window). No separate "just fired" event or
  // timestamp needs to be threaded through from client.js - the existing
  // freeze flag already carries that edge.
  //
  // Reuses gltf-tank-kit.js's own recoil state machine verbatim (kick phase
  // ramps 0->1 at 13/s, then return eases 1->0 at 5/s - see its recoilPhase
  // 'kick'/'return' handling) rather than inventing a new timing curve, kept
  // as a real per-tank dt-driven state machine (not just a fixed-duration
  // tween off the fire timestamp) for the same reason the 3D version is:
  // frame-rate independence. One state entry per tank id, so many tanks
  // recoil independently and a tank that hasn't fired this session simply
  // has no entry (treated as recoil 0, see drawTankSprite's `opts.id == null`
  // guard above and the `has(id)` check below).
  const recoilStateById = new Map(); // id -> {phase:'idle'|'kick'|'return', recoil, wasStopped, lastT}
  const RECOIL_KICK_RATE = 13; // /s, matches gltf-tank-kit.js's `recoil + dt*13`
  const RECOIL_RETURN_RATE = 5; // /s, matches gltf-tank-kit.js's `recoil - dt*5`
  // Baked-frame pixels (of the turret's own 160x160 frame, see FRAME_SIZE in
  // tank-bake.js) the turret sprite translates backward at full recoil.
  // gltf-tank-kit.js's 3D version kicks back `0.07 * scale` world units,
  // where `scale` is the model's own normalize-pass scale factor (footprint
  // ~2.15 units) - i.e. roughly 7% of the tank's own size. There's no exact
  // pixel analog of that world-unit `scale` here, so this picks a plain
  // frame-pixel constant in the same spirit ("a small, clearly visible kick,
  // not a redesign of the silhouette") rather than reverse-engineering an
  // exact ratio: 8px against a 160px frame is close to that same ballpark.
  const RECOIL_KICK_FRAME_PX = 8;

  function updateRecoil(id, stopped) {
    const now = performance.now() / 1000;
    let s = recoilStateById.get(id);
    if (!s) {
      s = { phase: 'idle', recoil: 0, wasStopped: stopped, lastT: now };
      recoilStateById.set(id, s);
    }
    const dt = Math.min(now - s.lastT, 0.1); // clamp a tab-backgrounded/slow frame
    s.lastT = now;

    if (stopped && !s.wasStopped) { s.phase = 'kick'; s.recoil = 0; }
    s.wasStopped = stopped;

    if (s.phase === 'kick') {
      s.recoil = Math.min(s.recoil + dt * RECOIL_KICK_RATE, 1);
      if (s.recoil >= 1) s.phase = 'return';
    } else if (s.phase === 'return') {
      s.recoil = Math.max(s.recoil - dt * RECOIL_RETURN_RATE, 0);
      if (s.recoil <= 0) s.phase = 'idle';
    }
    return s.recoil;
  }

  async function load() {
    try {
      const res = await fetch(MANIFEST_URL);
      manifest = await res.json();

      hullFrameByKey = new Map();
      manifest.hull.frames.forEach((f) => hullFrameByKey.set(frameKey(f.headingIndex, f.colorIndex), f));

      turretFrameByKey = new Map();
      manifest.turret.frames.forEach((f) => turretFrameByKey.set(frameKey(f.headingIndex, f.colorIndex), f));

      hullImage = new Image();
      hullImage.onload = () => { hullImageReady = true; };
      hullImage.onerror = () => console.error('[tank-sprites] failed to load', HULL_ATLAS_URL);
      hullImage.src = HULL_ATLAS_URL;

      turretImage = new Image();
      turretImage.onload = () => { turretImageReady = true; };
      turretImage.onerror = () => console.error('[tank-sprites] failed to load', TURRET_ATLAS_URL);
      turretImage.src = TURRET_ATLAS_URL;
    } catch (err) {
      console.error('[tank-sprites] failed to load manifest', err);
    }
  }
  load();

  // Nearest baked heading to a live angle, matched against each heading's
  // own *apparent on-screen* forward angle (screenAngle, baked into the
  // manifest) rather than its raw 3D yaw - the bake camera is a fixed
  // elevated 3/4 rig, not a true top-down view, so the two aren't linearly
  // related (see tank-bake.js). Only 64 entries per layer - a linear scan
  // per call is cheap. Shared by both the hull (hullAngle) and turret
  // (turretAngle) layers - each has its own baked headings array, but the
  // matching technique is identical.
  function nearestHeadingIndex(headings, angle) {
    let best = 0;
    let bestDist = Infinity;
    for (let i = 0; i < headings.length; i++) {
      const a = headings[i].screenAngle;
      let d = Math.abs(a - angle) % (Math.PI * 2);
      if (d > Math.PI) d = Math.PI * 2 - d;
      if (d < bestDist) { bestDist = d; best = i; }
    }
    return best;
  }

  /**
   * Drop-in replacement for client.js's old drawTank() body (client.js:
   * 1250-1287) - draws both the hull and turret as independently-rotating
   * baked sprite layers, composited on top of each other, in the tank's own
   * local space (caller has already translate()d to the tank's screen
   * position; x/y here are normally 0,0, kept as explicit params in case a
   * caller ever needs an offset).
   *
   * @param {CanvasRenderingContext2D} ctx
   * @param {number} x local origin x (tank position, usually 0)
   * @param {number} y local origin y (tank position, usually 0)
   * @param {number} hullAngle radians, world/screen atan2 convention (see
   *   client.js's worldToScreen/getRenderPos - 0 = +x, y-down)
   * @param {number} turretAngle radians, same convention - independent of
   *   hullAngle, matches the old primitive turret's `ctx.rotate(turretAngle)`
   *   (an absolute screen angle, not relative to the hull)
   * @param {number} colorIndex index into TANK_COLORS/manifest.hull.colors
   * @param {object} [opts]
   * @param {number} [opts.zoom] current ZOOM factor (default 1)
   * @param {boolean} [opts.stopped] draws the "reload" ring, same as before,
   *   and doubles as the recoil trigger - see updateRecoil() below.
   * @param {string|number} [opts.id] stable per-tank identity (player id, or
   *   any constant for the local tank) used to keep each tank's own recoil
   *   kick/return timer independent of every other tank's. Recoil is skipped
   *   (turret drawn at rest) if omitted.
   */
  function drawTankSprite(ctx, x, y, hullAngle, turretAngle, colorIndex, opts) {
    opts = opts || {};
    const zoom = opts.zoom == null ? 1 : opts.zoom;
    const recoil = opts.id == null ? 0 : updateRecoil(opts.id, !!opts.stopped);

    let hullHeadingIndex = null;
    let ci = 0;

    // --- hull: baked sprite, nearest heading bucket + exact color match ---
    if (hullImageReady && manifest && hullFrameByKey) {
      hullHeadingIndex = nearestHeadingIndex(manifest.hull.headings, hullAngle);
      ci = (colorIndex >= 0 && colorIndex < manifest.hull.colors.length) ? colorIndex : 0;
      const frame = hullFrameByKey.get(frameKey(hullHeadingIndex, ci));
      if (frame) {
        const drawW = manifest.hull.frameWidth * SPRITE_SCALE * zoom;
        const drawH = manifest.hull.frameHeight * SPRITE_SCALE * zoom;
        const destX = x - (manifest.hull.anchor.x / manifest.hull.frameWidth) * drawW;
        const destY = y - (manifest.hull.anchor.y / manifest.hull.frameHeight) * drawH;
        ctx.drawImage(hullImage, frame.x, frame.y, frame.w, frame.h, destX, destY, drawW, drawH);
      }
    }
    // else: sprite atlas hasn't finished loading yet - skip the hull this
    // frame rather than drawing a fallback shape (no fallback shapes is a
    // deliberate choice, see wild-percolating-breeze.md).

    if (opts.stopped) {
      ctx.save();
      ctx.translate(x, y);
      ctx.beginPath();
      ctx.strokeStyle = 'rgba(255,210,80,0.9)';
      ctx.lineWidth = 3;
      ctx.arc(0, 0, 30 * zoom, 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();
    }

    // --- turret: baked sprite, positioned at this hull heading's mount
    // point (manifest.hull.headings[h].turretMount - baked per hull heading
    // since the mount orbits with the hull), independently rotated to the
    // nearest baked heading for the live turretAngle. Skipped entirely if
    // either atlas hasn't loaded yet or the hull heading (and so its mount
    // point) couldn't be resolved this frame. ---
    if (turretImageReady && manifest && turretFrameByKey && hullHeadingIndex != null) {
      const turretHeadingIndex = nearestHeadingIndex(manifest.turret.headings, turretAngle);
      const tci = manifest.turret.hasColorAxis ? ci : 0;
      const tframe = turretFrameByKey.get(frameKey(turretHeadingIndex, tci));
      if (tframe) {
        const scale = SPRITE_SCALE * zoom;
        const mount = manifest.hull.headings[hullHeadingIndex].turretMount;
        // Where the turret's mount point lands on screen, given the CURRENT
        // hull heading (see file header comment for the derivation - any
        // pixel P within the hull frame lands at
        // x + (P - hull.anchor) * scale, since the hull frame itself is
        // drawn so hull.anchor lands exactly on (x,y)).
        let turretScreenX = x + (mount.x - manifest.hull.anchor.x) * scale;
        let turretScreenY = y + (mount.y - manifest.hull.anchor.y) * scale;

        // Recoil kick (PHASE 4): a pure translation of the already-rotated
        // turret sprite along its own barrel direction, no re-baking - see
        // updateRecoil() below for the timing curve. turretAngle is already
        // the absolute on-screen barrel direction (the whole reason the
        // Phase-2 180deg-fix mattered), so unlike gltf-tank-kit.js's 3D
        // recoil - which has to approximate "forward" from the turret
        // pivot's offset from the hull center, see turretForwardLocal there
        // - this can just use (cos, sin) of it directly, no approximation.
        if (recoil > 0) {
          const kickPx = recoil * RECOIL_KICK_FRAME_PX;
          turretScreenX -= Math.cos(turretAngle) * kickPx * scale;
          turretScreenY -= Math.sin(turretAngle) * kickPx * scale;
        }

        const drawW = manifest.turret.frameWidth * scale;
        const drawH = manifest.turret.frameHeight * scale;
        const destX = turretScreenX - (manifest.turret.anchor.x / manifest.turret.frameWidth) * drawW;
        const destY = turretScreenY - (manifest.turret.anchor.y / manifest.turret.frameHeight) * drawH;
        ctx.drawImage(turretImage, tframe.x, tframe.y, tframe.w, tframe.h, destX, destY, drawW, drawH);
      }
    }

    // --- pupils: live, drawn last (on top of both baked layers) - see file
    // header comment. Gated on the hull heading having resolved (needs its
    // baked eye-socket coords); independent of the turret atlas having
    // loaded, since the gaze angle itself doesn't need the turret sprite. ---
    if (hullHeadingIndex != null) {
      drawPupils(ctx, x, y, manifest.hull.headings[hullHeadingIndex], hullAngle, turretAngle, SPRITE_SCALE * zoom);
    }
  }

  // Rookie's own eyeSepFrac (tank-concepts/tank-08-rookie.js's buildGLTFTank
  // call, cfg.eyeSepFrac: 0.17 - overrides gltf-tank-kit.js's 0.16 default).
  // Needed to translate buildEyeRig()'s `frontWidth`-relative ratios
  // (0.05 for the gaze offset, 0.048 for the pupil radius) into a ratio of
  // eye-to-eye separation instead: buildEyeRig(frontWidth, eyeSepX=...) is
  // always called with eyeSepX = frontWidth * eyeSepFrac, so the full
  // mount-to-mount separation is 2*eyeSepX = frontWidth * (2*eyeSepFrac), and
  // frontWidth = eyeSeparation / (2*eyeSepFrac). Substituting into
  // `px = Math.sin(gazeYaw) * 0.05 * frontWidth` gives
  // `px = Math.sin(gazeYaw) * (0.05 / (2*eyeSepFrac)) * eyeSeparation` - a
  // ratio of eyeSeparation, which this file already has a correctly
  // per-heading-foreshortened pixel-space stand-in for: the baked distance
  // between manifest.hull.headings[h].eyeLeft/eyeRight (same fixed bake
  // camera as everything else, so already right for this heading, no extra
  // projection math needed here). Cross-checked against the sclera actually
  // visible in the baked hull PNG rather than assumed, per the plan: at
  // heading 0, eyeSepPx is 25.15px and the sclera measures ~7.4-7.5px
  // radius there, matching eyeSepPx * 0.1/(2*0.17) = 7.4px almost exactly -
  // so PUPIL_RADIUS_RATIO (which uses the same 2*0.17 denominator, just
  // buildEyeRig()'s 0.048 pupil ratio instead of its 0.1 sclera ratio) is
  // trustworthy in this pixel space too.
  const ROOKIE_EYE_SEP_FRAC = 0.17;
  const GAZE_OFFSET_RATIO = 0.05 / (2 * ROOKIE_EYE_SEP_FRAC);
  const PUPIL_RADIUS_RATIO = 0.048 / (2 * ROOKIE_EYE_SEP_FRAC);

  const PUPIL_COLOR = '#14120f'; // gltf-tank-kit.js's eyeBlackMat base color

  /**
   * Draws both pupils for the current hull heading, offset toward the
   * turret's gaze direction. `heading` is this frame's chosen
   * manifest.hull.headings[] entry (has .eyeLeft/.eyeRight, baked frame-
   * pixel coords). `scale` is the same SPRITE_SCALE*zoom used to place the
   * hull/turret frames, so a frame-pixel offset here lands consistently
   * with them.
   */
  function drawPupils(ctx, x, y, heading, hullAngle, turretAngle, scale) {
    const eyeLeft = heading.eyeLeft;
    const eyeRight = heading.eyeRight;
    if (!eyeLeft || !eyeRight) return;

    // The two eye mounts sit apart purely along the hull's local left/right
    // axis (buildEyeRig's baseX = +-eyeSepX, same y/z) - so the baked
    // eyeLeft->eyeRight screen vector for this heading IS the projection of
    // that local right axis onto the screen, already correctly rotated for
    // however this heading looks on screen (no separate screenAngle lookup
    // needed - this vector already bakes that in, the same way turretMount
    // positioning above reuses baked pixel coords instead of re-deriving a
    // rotation from scratch).
    const dx = eyeRight.x - eyeLeft.x;
    const dy = eyeRight.y - eyeLeft.y;
    const eyeSepPx = Math.hypot(dx, dy);
    if (eyeSepPx < 0.01) return;
    const rightX = dx / eyeSepPx;
    const rightY = dy / eyeSepPx;

    // gazeYaw: turret yaw relative to chassis - both hullAngle/turretAngle
    // are already absolute screen-space angles here, so their difference is
    // the 2D analog of buildEyeRig()'s 3D relative yaw. Math.sin() saturates
    // the offset near +-90deg on its own (see buildEyeRig()'s own comment) -
    // deliberately no extra clamp here, mirroring that reasoning.
    //
    // Sign: naively porting buildEyeRig()'s `+sin(gazeYaw)` along the baked
    // eyeLeft->eyeRight axis looked right in isolated screenshots but was
    // backwards in play - confirmed by checking the real bake data instead
    // of re-deriving the 3D camera math by hand: for every one of the 32
    // baked headings, rotating that heading's own forward vector
    // (cos/sin(screenAngle)) by +90deg lands almost exactly opposite the
    // baked (eyeRight-eyeLeft) pixel vector (dot product averages -0.995
    // across all headings, checked via a one-off node script against
    // rookie-manifest.json). I.e. the bake camera's fixed 3/4 elevated
    // viewpoint (see tank-bake.js's viewDir) happens to mirror the hull's
    // own left/right onto screen left/right versus the flat +90/-90 rule a
    // top-down camera would give - so a positive gazeYaw needs the OPPOSITE
    // sign here to still mean "shift toward the side the turret swung to".
    const gazeYaw = turretAngle - hullAngle;
    const offsetPx = -Math.sin(gazeYaw) * GAZE_OFFSET_RATIO * eyeSepPx;
    const pupilRadius = PUPIL_RADIUS_RATIO * eyeSepPx * scale;

    ctx.save();
    ctx.fillStyle = PUPIL_COLOR;
    [eyeLeft, eyeRight].forEach((socket) => {
      // Same frame-pixel -> screen-pixel placement as the hull/turret draws
      // above ((point - anchor) * scale relative to the tank's screen
      // origin), plus the gaze offset - computed in frame-pixel space, then
      // scaled once here, same as everything else in this function.
      const sx = x + (socket.x - manifest.hull.anchor.x) * scale + rightX * offsetPx * scale;
      const sy = y + (socket.y - manifest.hull.anchor.y) * scale + rightY * offsetPx * scale;
      ctx.beginPath();
      ctx.arc(sx, sy, pupilRadius, 0, Math.PI * 2);
      ctx.fill();
    });
    ctx.restore();
  }

  return { drawTankSprite };
})();
